import Anchor from 'components/Basic/Anchor/Anchor';
import Collapse from 'components/Basic/Collapse/Collapse';
import { ExtendedNextLink } from 'components/Basic/ExtendedNextLink/ExtendedNextLink';
import AlternativeUrls from 'components/Basic/Head/AlternativeUrls/AlternativeUrls';
import { ProductMetadata } from 'components/Basic/Head/ProductMetadata/ProductMetadata';
import Image from 'components/Basic/Image/Image';
import StickyBar from 'components/Basic/StickyBar/StickyBar';
import Table from 'components/Basic/Table/Table';
import { YouTubeVideo } from 'components/Basic/YouTubeVideo/YouTubeVideo';
import { FaqAccordion } from 'components/Blocks/FaqAccordion/FaqAccordion';
import { ProductStock } from 'components/Blocks/Product/Availability/ProductStock';
import ProductDetailFlags from 'components/Blocks/Product/Flags/ProductDetailFlags';
import { GaranNestedLabel } from 'components/Blocks/Product/Garan/GaranNestedLabel';
import { ProductWishlistButton } from 'components/Blocks/Product/Wishlist/ProductWishlistButtons';
import { ProductLineWrapper } from 'components/Blocks/ProductLine/ProductLineWrapper';
import { ProductRecommendationAreas } from 'components/Blocks/ProductRecommendationArea/ProductRecommendationAreas';
import { ProductReviews } from 'components/Blocks/Reviews/ProductReviews';
import { ReviewSummaryPreview } from 'components/Blocks/Reviews/ReviewSummaryPreview';
import LimitHeightWrap from 'components/Helpers/LimitHeightWrap/LimitHeightWrap';
import UserText from 'components/Helpers/UserText/UserText';
import { Webline } from 'components/Layout/Webline/Webline';
import { ColorVariants } from 'components/Pages/ProductDetail/ColorVariants/ColorVariants';
import ProductDetailAddToCart from 'components/Pages/ProductDetail/ProductDetailAddToCart/ProductDetailAddToCart';
import ProductDetailBuyInEshop from 'components/Pages/ProductDetail/ProductDetailAddToCart/ProductDetailBuyInEshop';
import { ProductDetailAdvertBanner } from 'components/Pages/ProductDetail/ProductDetailAdvertBanner';
import ProductDetailAvailability from 'components/Pages/ProductDetail/ProductDetailAvailability/ProductDetailAvailability';
import ProductDetailFiles from 'components/Pages/ProductDetail/ProductDetailFiles/ProductDetailFiles';
import { ProductDetailGallery } from 'components/Pages/ProductDetail/ProductDetailGallery';
import ProductDetailSpareParts from 'components/Pages/ProductDetail/ProductDetailSpareParts/ProductDetailSpareParts';
import ProductDetailStickyBar from 'components/Pages/ProductDetail/ProductDetailStickyBar/ProductDetailStickyBar';
import { VirtualSet } from 'components/Pages/ProductDetail/VirtualSet/VirtualSet';
import { mapAddToCartPopupData } from 'connectors/cart/Cart';
import { useSettings } from 'connectors/Settings/Settings';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { createNumberAriaLabel } from 'helpers/accessibility/ariaLabelHelpers';
import { removeLinks } from 'helpers/formaters/formatText';
import { getTranslateFromLogicalValue } from 'helpers/getTranslateFromLogicalValue';
import { isB2bDomain, isB2cDomain, isDomainLocale } from 'helpers/isDomainLocale';
import { isGaranVisible } from 'helpers/isGaranVisible';
import { getUrlWithoutGetParameters, replaceLeadingDoubleSlash } from 'helpers/parsing/urlParsing';
import { useAddToCart } from 'hooks/cart/useAddToCart';
import { useGtmProductDetailView } from 'hooks/gtm/useGtmProductDetailView';
import { useSetLastVisitedProductCatalogNumbers } from 'hooks/lastVisited/useSetLastVisitedProductCatalogNumbers';
import { usePersoo } from 'hooks/persoo/usePersoo';
import { useEffectOnce } from 'hooks/ui/useEffectOnce';
import { useMediaMin } from 'hooks/ui/useMediaMin';
import { useSticky } from 'hooks/ui/useSticky';
import { useWishlist } from 'hooks/wishlist/useWishlist';
import { createPersooEventForAddToCart } from 'modules/persoo/persooEvents';
import { PERSOO_IDS } from 'modules/persoo/persooTypes';
import { Translate } from 'next-translate';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { ArrowIcon, CheckIcon } from 'public/svg/IconsSvg';
import React, { Dispatch, Fragment, RefObject, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import * as smoothscroll from 'smoothscroll-polyfill';
import { usePersistStore } from 'store/usePersistStore';
import { twJoin } from 'tailwind-merge';
import { GtmMessageOriginType } from 'types/gtm';
import { ProductDetailType, VariantDetailType } from 'types/product';

type ProductDetailProps = {
    fetching: boolean;
    product: ProductDetailType | VariantDetailType;
};

const TEST_IDENTIFIER = 'pages-productdetail';
const PRODUCT_RECOMMENDATION_AREA_GTM_LIST_NAME = 'product recommendation area product detail page' as const;

const productDetailCodeTwClass = 'text-tiny leading-6 text-primary';
const productNameTwClass = 'text-h4 font-bold text-primary';

const ProductDetail: FC<ProductDetailProps> = ({ fetching, product }) => {
    const { t } = useTranslation();
    const settings = useSettings();
    const domainConfig = useDomainConfig();
    const { isPersooAllowed } = usePersoo();

    const scrollTarget = useRef<HTMLUListElement>(null);
    const { asPath } = useRouter();
    useGtmProductDetailView(product, getUrlWithoutGetParameters(asPath), fetching);

    const { currencyCode, type: domainType, defaultLocale } = useDomainConfig();
    const isItalianLocale = isDomainLocale(defaultLocale, 'it');
    const isEnglishLocale = isDomainLocale(defaultLocale, 'en');
    const virtualSetRef = useRef<HTMLDivElement>(null);
    const overviewRef = useRef<HTMLDivElement>(null);
    const filesRef = useRef<HTMLDivElement>(null);
    const sparePartsRef = useRef<HTMLDivElement>(null);
    const productLineRef = useRef<HTMLDivElement>(null);
    const recommendationAreaRef = useRef<HTMLDivElement>(null);
    const reviewsRef = useRef<HTMLDivElement | null>(null);
    const toTopContentRef = useRef<HTMLDivElement>(null);
    const [isProductBarSticky, , stickyRef] = useSticky(null, [asPath]);

    const [isOpenVirtualSet, setIsOpenVirtualSet] = useState(true);
    const [isOpenOverview, setIsOpenOverview] = useState(false);
    const [isOpenProductFiles, setIsOpenProductFiles] = useState(false);
    const [isOpenReviews, setIsOpenReviews] = useState(false);
    const [isOpenProductSpareParts, setIsOpenProductSpareParts] = useState(false);
    const [isOpenProductLine, setIsOpenProductLine] = useState(false);
    const [heightUpdateCounter, setHeightUpdateCounter] = useState(0);
    const [reviewsHeightUpdateCounter, setReviewsHeightUpdateCounter] = useState(0);
    const [productLineHeightUpdateCounter, setProductLineHeightUpdateCounter] = useState(0);
    const isMobile = !useMediaMin('sm');
    const isTablet = !useMediaMin('lg');
    const firstAreaWithProducts = product.recommendationAreas.find((area) => area.products.length > 0);
    const isGaranBlockVisible = settings?.isGaranLabelAllowed && isGaranVisible(product.warrantyYears);
    const visiblePictogramParameters =
        product.pictogramParameters?.filter(
            (pictogramParameter) => !(pictogramParameter.isWarrantyType && isGaranBlockVisible),
        ) ?? [];

    useSetLastVisitedProductCatalogNumbers(product.catalogNumber);

    const [changeCartItemQuantity, isAddToCartFetching] = useAddToCart(GtmMessageOriginType.product, 'product');
    const { updateUserState } = usePersistStore((s) => s);
    const { toggleProductInWishlist, isProductInWishlist } = useWishlist();

    const onAddToCartHandler = useCallback(async () => {
        const addToCartResult = await changeCartItemQuantity(product.uuid, 1, 1, 'detail');
        const mappedAddToCartResult = mapAddToCartPopupData(addToCartResult, currencyCode);
        if (mappedAddToCartResult) {
            createPersooEventForAddToCart(mappedAddToCartResult.catalogNumber);
            updateUserState({
                addToCartPopupData: {
                    gtmListName: 'detail',
                    listIndex: 1,
                    product: mappedAddToCartResult,
                },
            });
        }
    }, [changeCartItemQuantity, currencyCode, product.uuid, updateUserState]);

    const onAnchorClickHandler = useCallback(
        (
            passedRef: RefObject<HTMLDivElement>,
            setPassedState?: Dispatch<SetStateAction<boolean>>,
            deltaOffsetTop?: number,
        ) => {
            if (typeof setPassedState === 'function') {
                setPassedState(true);
            }

            if (passedRef.current === null) {
                return;
            }

            let targetOffsetTop = passedRef.current.offsetTop;
            targetOffsetTop -= stickyRef?.current?.offsetHeight ?? 0;
            targetOffsetTop -= deltaOffsetTop ?? 0;

            window.scrollTo({ top: targetOffsetTop, behavior: 'smooth' });
        },
        [stickyRef],
    );

    useEffectOnce(() => smoothscroll.polyfill());

    useEffect(() => {
        setIsOpenOverview(isMobile);
    }, [isMobile]);

    return (
        // the key helps to re-mount the component when navigating between different products, which prevents the components from keeping an unwanted state
        <Fragment key={product.uuid}>
            <ProductMetadata product={product} />
            <AlternativeUrls alternativeUrls={product.alternateUrls} />
            <Webline>
                <div
                    className="mb-10 flex flex-col flex-wrap lg:mb-2 lg:flex-row"
                    data-testid={
                        'colorVariants' in product ? `${TEST_IDENTIFIER}-variant-${product.catalogNumber}` : ''
                    }
                >
                    {isTablet && (
                        <h1
                            className={twJoin(productNameTwClass, 'mb-2 lg:hidden')}
                            data-testid={`${TEST_IDENTIFIER}-name`}
                        >
                            {product.name}
                        </h1>
                    )}
                    <div
                        data-testid={`${TEST_IDENTIFIER}-gallery`}
                        className={twJoin(
                            'relative mb-7 lg:mb-0 lg:w-[calc(100%-346px)] vl:w-[calc(100%-508px)]',
                            '[&>div]:w-full [&>div]:overflow-hidden [&>div]:text-[0]', // this styled is used to style the div inserted by the lightbox gallery package
                        )}
                    >
                        <ProductDetailGallery
                            flags={product.flags}
                            images={product.images}
                            productName={product.name}
                            youtubeVideoId={product.youtubeVideoId}
                        />
                        <ProductDetailFlags isProductDetail flags={product.flags} product={product} />
                    </div>
                    <div className="w-full lg:w-[346px] lg:pl-7 vl:w-[508px] vl:pl-10 [&>*:last-child]:mb-0">
                        {!isTablet && (
                            <h1
                                data-testid={`${TEST_IDENTIFIER}-name`}
                                className={twJoin(
                                    productNameTwClass,
                                    'mb-1 hidden lg:block vl:text-[30px] vl:leading-9',
                                )}
                            >
                                {product.name}
                            </h1>
                        )}
                        {product.benefits.length > 0 && product.benefits[0] !== '' && (
                            <ul className="pb-2 pt-1" data-testid={`${TEST_IDENTIFIER}-short-description`}>
                                {product.benefits.map((item: string, key) => (
                                    <li key={key} className="mb-2 flex text-body font-bold leading-6">
                                        <CheckIcon className="mr-4 mt-1 h-3.5 w-4 flex-shrink-0" /> {item}
                                    </li>
                                ))}
                            </ul>
                        )}
                        <div
                            className={twJoin(
                                isB2bDomain(domainConfig) && product.ean ? 'mb-1' : 'mb-6',
                                'flex content-start',
                            )}
                        >
                            <div
                                aria-label={`${t('Product code')} ${createNumberAriaLabel(product.catalogNumber)}`}
                                className={productDetailCodeTwClass}
                                data-testid={`${TEST_IDENTIFIER}-code`}
                            >
                                {t('Product code')} {product.catalogNumber}
                            </div>
                            {product.brand && (
                                <div className={twJoin(productDetailCodeTwClass, 'ml-6 vl:ml-9')}>
                                    {`${t('Product line')}: `}
                                    <ExtendedNextLink className="text-primary" href={product.brand.slug} type="brand">
                                        {product.brand.name}
                                    </ExtendedNextLink>
                                </div>
                            )}
                        </div>
                        {isB2bDomain(domainConfig) && product.ean && (
                            <div className={twJoin(productDetailCodeTwClass, 'mb-5')}>
                                {t('EAN')}: {product.ean}
                            </div>
                        )}
                        {'colorVariants' in product &&
                            'currentColorVariant' in product &&
                            product.currentColorVariant !== null && (
                                <ColorVariants
                                    colorVariants={product.colorVariants}
                                    currentVariantUuid={product.currentColorVariant.uuid}
                                />
                            )}
                        <div className="-ml-2 -mt-2 mb-7 flex vl:mb-6">
                            {product.reviewsSummary.totalCount > 0 && (
                                <Anchor
                                    text={<ReviewSummaryPreview summary={product.reviewsSummary} />}
                                    onClickHandler={() => onAnchorClickHandler(reviewsRef, setIsOpenReviews)}
                                />
                            )}
                            <Anchor
                                text={t('Description')}
                                onClickHandler={() => onAnchorClickHandler(toTopContentRef, setIsOpenOverview)}
                            />
                        </div>
                        {!product.isSaleExclusion && isB2cDomain(domainConfig) && (
                            <ProductDetailAvailability product={product} scrollTarget={scrollTarget} />
                        )}
                        {!product.isSaleExclusion && isB2bDomain(domainConfig) && (
                            <ProductStock
                                className="mb-5 text-h6 font-bold leading-6"
                                minimumSellingQuantity={product.b2bMinimumProductOrder ?? 1}
                                quantity={product.stockQuantity}
                                supplyDate={product.supplyDate}
                            />
                        )}
                        {!product.isSaleExclusion && domainType !== 'web' && (
                            <ProductDetailAddToCart gtmListName="detail" isMobile={isMobile} product={product} />
                        )}
                        {product.isSaleExclusion && domainType !== 'web' && (
                            <div className="font-bold text-red">{t('This product is no longer sold')}</div>
                        )}

                        {domainType === 'web' && (isItalianLocale || isEnglishLocale) && (
                            <ProductDetailBuyInEshop product={product} />
                        )}

                        <ProductWishlistButton
                            isWithText
                            className="my-7 mb-0"
                            isProductInWishlist={isProductInWishlist(product.uuid)}
                            toggleProductInWishlist={() => toggleProductInWishlist(product.uuid)}
                        />

                        <ProductDetailAdvertBanner />
                    </div>
                </div>
                {isGaranBlockVisible && product.warrantyYears !== null && (
                    <div className="mb-6">
                        <GaranNestedLabel catalogNumber={product.catalogNumber} warrantyYears={product.warrantyYears} />
                    </div>
                )}
                {visiblePictogramParameters.length > 0 && (
                    <div className="-ml-5 -mt-5 mb-14 flex flex-wrap">
                        {visiblePictogramParameters.map((pictogramParameter) => (
                            <div
                                key={pictogramParameter.uuid}
                                className="ml-5 mt-5 flex flex-1 flex-row items-center text-center text-small font-normal leading-4 tracking-wider lg:w-[90px] lg:flex-none lg:flex-shrink-0 lg:flex-col"
                            >
                                {pictogramParameter.slug ? (
                                    <ExtendedNextLink
                                        className="flex flex-col items-center text-black no-underline"
                                        href={replaceLeadingDoubleSlash(pictogramParameter.slug)}
                                        target="_blank"
                                    >
                                        <>
                                            <div className="mr-4 h-11 w-11 flex-shrink-0 lg:mr-2 [&>div]:p-0">
                                                <Image
                                                    alt={pictogramParameter.name}
                                                    height="44"
                                                    src={pictogramParameter.image}
                                                    width="44"
                                                />
                                            </div>
                                            {pictogramParameter.name}
                                        </>
                                    </ExtendedNextLink>
                                ) : (
                                    <>
                                        <div className="mr-4 h-11 w-11 flex-shrink-0 lg:mr-2 [&>div]:p-0">
                                            <Image
                                                alt={pictogramParameter.name}
                                                height="44"
                                                src={pictogramParameter.image}
                                                width="44"
                                            />
                                        </div>
                                        {pictogramParameter.name}
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </Webline>
            {product.virtualSet && product.virtualSet.products.length > 0 && (
                <Webline data-testid={`${TEST_IDENTIFIER}-virtual-set`}>
                    <Collapse
                        data-testid={`${TEST_IDENTIFIER}-virtual-set-content`}
                        mobileTitle={t('Everything matches in this set - mobile')}
                        parentCounter={heightUpdateCounter}
                        parentRef={virtualSetRef}
                        parentState={isOpenVirtualSet}
                        setParentState={setIsOpenVirtualSet}
                    >
                        <VirtualSet virtualSet={product.virtualSet} />
                    </Collapse>
                </Webline>
            )}
            <StickyBar dataTestId={`${TEST_IDENTIFIER}-sticky-bar`} isSticky={isProductBarSticky} stickyRef={stickyRef}>
                <Webline className={twJoin(isProductBarSticky && 'pb-0.5 pt-0.5')}>
                    <ProductDetailStickyBar
                        isAddToCartFetching={isAddToCartFetching}
                        isSticky={isProductBarSticky}
                        product={product}
                        onAddToCart={onAddToCartHandler}
                    >
                        <div className="mb-0 hidden gap-y-1.5 vl:flex vl:max-w-[calc(100%-440px)] vl:flex-wrap">
                            {isProductBarSticky && (
                                <div
                                    className="mr-2 inline-flex h-8 w-8 cursor-pointer flex-row items-center justify-center rounded-2xl bg-gray text-[13px] font-bold leading-4 tracking-wide hover:bg-grayMidDarker"
                                    onClick={() => onAnchorClickHandler(toTopContentRef)}
                                >
                                    <ArrowIcon className="rotate-180" />
                                </div>
                            )}
                            <Anchor
                                text={t('Description and parameters')}
                                onClickHandler={() => onAnchorClickHandler(toTopContentRef, setIsOpenOverview)}
                            />
                            {product.files.length > 0 && (
                                <Anchor
                                    text={t('Manual')}
                                    onClickHandler={() => onAnchorClickHandler(filesRef, undefined, 20)}
                                />
                            )}
                            {product.reviewsSummary.totalCount > 0 && (
                                <Anchor
                                    text={
                                        <ReviewSummaryPreview
                                            summary={product.reviewsSummary}
                                            textType={isProductBarSticky ? 'countOnly' : 'fullText'}
                                        />
                                    }
                                    onClickHandler={() => onAnchorClickHandler(reviewsRef, setIsOpenReviews)}
                                />
                            )}
                            {firstAreaWithProducts && (
                                <Anchor
                                    text={firstAreaWithProducts.area.header}
                                    onClickHandler={() => onAnchorClickHandler(recommendationAreaRef, undefined, 20)}
                                />
                            )}
                            {product.spareParts.length > 0 && (
                                <Anchor
                                    text={t('Spare parts')}
                                    onClickHandler={() => onAnchorClickHandler(sparePartsRef, undefined, 20)}
                                />
                            )}

                            {product.brand && (
                                <Anchor
                                    text={t('Product line')}
                                    onClickHandler={() => onAnchorClickHandler(productLineRef, undefined, 20)}
                                />
                            )}
                        </div>
                    </ProductDetailStickyBar>
                </Webline>
            </StickyBar>
            <div ref={toTopContentRef} />
            <div className="mb-5 pb-12">
                <Webline data-testid={`${TEST_IDENTIFIER}-description`}>
                    <Collapse
                        data-testid={`${TEST_IDENTIFIER}-overview-content`}
                        mobileTitle={t('Description and parameters')}
                        parentCounter={heightUpdateCounter}
                        parentRef={overviewRef}
                        parentState={isOpenOverview}
                        setParentState={setIsOpenOverview}
                    >
                        <div className="flex flex-col items-start vl:flex-row">
                            <div className="mb-10 vl:mb-0 vl:w-[calc(100%-460px)]">
                                <LimitHeightWrap
                                    isWithButtonIcons
                                    buttonAlign="center"
                                    buttonClassName="translate-y-12"
                                    buttonLabelLess={t('Read less')}
                                    buttonLabelMore={t('Full description')}
                                    increaseParentCounter={() => setHeightUpdateCounter((prev) => prev + 1)}
                                    isDefaultEnabled={false}
                                    mobileHeight={570}
                                >
                                    <UserText
                                        htmlContent={
                                            isB2bDomain(domainConfig)
                                                ? removeLinks(product.description)
                                                : product.description
                                        }
                                    />
                                    {product.producerInfo && (
                                        <div className="py-[30px]">
                                            <UserText htmlContent={product.producerInfo} />
                                        </div>
                                    )}
                                    <FaqAccordion items={product.faq} />
                                    <YouTubeVideo youtubeVideoId={product.youtubeVideoId} />
                                </LimitHeightWrap>
                            </div>
                            <div className="mb-7 w-full vl:ml-14 vl:w-[460px] vl:rounded-md vl:border-5 vl:border-grayLight vl:p-10">
                                {product.parameterImage && product.parameterImage[0] && (
                                    <div className="h-[340px] pb-10 vl:h-[400px] vl:pb-5">
                                        <Image
                                            alt={t('Parameter image')}
                                            height="400"
                                            src={product.parameterImage[0]}
                                            width="340"
                                        />
                                    </div>
                                )}

                                {product.groupedParameters.length > 0 && (
                                    <div className="-mt-9">
                                        {product.groupedParameters.map((group, index) => (
                                            <React.Fragment key={index}>
                                                <div className="mb-4 mt-9 text-h6 font-medium leading-6">
                                                    {group.name}
                                                </div>
                                                <Table>
                                                    <tbody>
                                                        {group.parameters
                                                            .filter((parameter) =>
                                                                parameter.values.some((value) => value.text),
                                                            )
                                                            .map((parameter) => (
                                                                <tr key={parameter.uuid}>
                                                                    <td>{parameter.name}</td>
                                                                    <th>
                                                                        {parameter.values.map((value, index) =>
                                                                            formatParameterValue(
                                                                                value.text ?? '',
                                                                                parameter.isLogicalType,
                                                                                index,
                                                                                t,
                                                                            ),
                                                                        )}
                                                                    </th>
                                                                </tr>
                                                            ))}
                                                    </tbody>
                                                </Table>
                                            </React.Fragment>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </Collapse>
                </Webline>
                {product.files.length > 0 && (
                    <Webline data-testid={`${TEST_IDENTIFIER}-manuals`}>
                        <Collapse
                            data-testid={`${TEST_IDENTIFIER}-manuals-content`}
                            mobileTitle={t('Product manuals')}
                            parentRef={filesRef}
                            parentState={isOpenProductFiles}
                            setParentState={setIsOpenProductFiles}
                        >
                            <div className="vl:pt-8">
                                <div className="bg-white vl:bg-grayLight vl:pb-8 vl:pl-12 vl:pr-12 vl:pt-8">
                                    <ProductDetailFiles files={product.files} />
                                </div>
                            </div>
                        </Collapse>
                    </Webline>
                )}
                {product.reviewsSummary.totalCount > 0 && (
                    <Webline>
                        <Collapse
                            mobileTitle={t('Reviews')}
                            parentCounter={reviewsHeightUpdateCounter}
                            parentRef={reviewsRef}
                            parentState={isOpenReviews}
                            setParentState={setIsOpenReviews}
                        >
                            <div className="vl:pt-8">
                                <div className="bg-white vl:bg-grayLight vl:pb-8 vl:pl-12 vl:pr-12 vl:pt-8">
                                    <ProductReviews
                                        productUuid={product.uuid}
                                        summary={product.reviewsSummary}
                                        increaseParentHeightCounter={() =>
                                            setReviewsHeightUpdateCounter((prev) => prev + 1)
                                        }
                                    />
                                </div>
                            </div>
                        </Collapse>
                    </Webline>
                )}
                {firstAreaWithProducts && (
                    <Webline data-testid={`${TEST_IDENTIFIER}-recommendationAreas`} parentRef={recommendationAreaRef}>
                        <div className="pt-8 vl:pl-12 vl:pr-12">
                            <ProductRecommendationAreas
                                prepareProductOrderOnMount
                                fallbackGtmId={PRODUCT_RECOMMENDATION_AREA_GTM_LIST_NAME}
                                productRecommendationAreas={product.recommendationAreas}
                                productSliderClassName="h-[500px] lg:h-[565px] vl:h-[575px]"
                            />
                        </div>
                    </Webline>
                )}
                {product.spareParts.length > 0 && (
                    <Webline data-testid={`${TEST_IDENTIFIER}-spareParts`}>
                        <Collapse
                            data-testid={`${TEST_IDENTIFIER}-spareParts`}
                            mobileTitle={t('Spare parts')}
                            parentRef={sparePartsRef}
                            parentState={isOpenProductSpareParts}
                            setParentState={setIsOpenProductSpareParts}
                        >
                            <div className={twJoin(!firstAreaWithProducts && 'pt-8', 'vl:pl-12 vl:pr-12')}>
                                <ProductDetailSpareParts spareParts={product.spareParts} />
                            </div>
                        </Collapse>
                    </Webline>
                )}
                {product.brand && (
                    <Webline>
                        <Collapse
                            mobileTitle={t('Product line')}
                            parentCounter={productLineHeightUpdateCounter}
                            parentRef={productLineRef}
                            parentState={isOpenProductLine}
                            setParentState={setIsOpenProductLine}
                        >
                            <div
                                className={twJoin(
                                    product.spareParts.length === 0 && !firstAreaWithProducts && 'vl:pt-8',
                                )}
                            >
                                <div className="bg-white vl:bg-grayLight vl:pb-8 vl:pl-12 vl:pr-12 vl:pt-8">
                                    <ProductLineWrapper
                                        brand={product.brand}
                                        parentRef={productLineRef}
                                        productDetailUuid={product.uuid}
                                        increaseParentHeightCounter={() =>
                                            setProductLineHeightUpdateCounter((prev) => prev + 1)
                                        }
                                    />
                                </div>
                            </div>
                        </Collapse>
                    </Webline>
                )}
            </div>
            <div id={isPersooAllowed ? PERSOO_IDS.detailAlternatives : undefined} />
        </Fragment>
    );
};

const formatParameterValue = (valueText: string, isLogicalType: boolean, index: number, t: Translate) => {
    const value = isLogicalType ? getTranslateFromLogicalValue(valueText, t) : valueText;

    return index > 0 ? ' | ' + value : value;
};

export default ProductDetail;
